/**
 * SignDispatcher — the queue, the slot accounting, and which worker runs which sign.
 *
 * Split from PiezoWorkerPool because the two answer different questions. This file answers "given the
 * workers that exist, who runs this job"; the pool answers "which workers exist". Apart, the routing rules
 * read without the spawn/deadline/reap machinery in the way, and vice versa.
 *
 * The dispatcher never creates or destroys a worker. When one has to go it stops routing to it and tells the
 * host, which owns termination and replacement.
 */

import { PiezoDebugPrefix } from '../provisioning/PiezoClient.constants';
import type { PiezoGetPiezoExecutionContextInput } from '../provisioning/PiezoClient.types';
import { MidwayAuthRequiredError } from './signWorkerProtocol';
import type { PoolToWorker, WorkerToPool } from './signWorkerProtocol';

/**
 * Per-sign deadline, measured from hand-off rather than enqueue: a queued job would otherwise spend its
 * budget waiting and expire having never run. Firing this evicts the worker, so it must stay well clear of
 * a slow-but-healthy sign.
 */
export const SIGN_TIMEOUT_MS = 10_000;
/**
 * Signs a single worker may run at once. More than one, because a sign is mostly a Witness network round
 * trip and a worker with a single slot idles through it.
 *
 * Deliberately small — slots are also what keeps the queue non-empty for workers that have not started
 * yet. A worker allowed to take a whole burst would drain the queue before its colleagues report READY.
 */
export const SLOTS_PER_WORKER = 2;
/**
 * Hand-offs allowed for one job, across workers: the original plus a bounded number of re-queues, so a
 * payload that wedges a worker cannot walk the whole pool.
 */
const MAX_JOB_ATTEMPTS = 2;
/** One sign, from submit() to settlement. `timer` is set only while a worker holds it. */
interface SignJob {
  readonly id: number;
  readonly input: PiezoGetPiezoExecutionContextInput;
  readonly resolve: (bundle: string) => void;
  readonly reject: (err: Error) => void;
  timer?: ReturnType<typeof setTimeout>;
  /** Hand-offs so far. Bounded by MAX_JOB_ATTEMPTS. */
  attempts: number;
}

/** Always before a job settles: a timer left armed evicts a healthy worker later. */
function disarm(job: SignJob): void {
  clearTimeout(job.timer);
  job.timer = undefined;
}

/** Deadline first, so no rejection path can forget it. Array, not Iterable: no downlevelIteration here. */
function failJobs(jobs: readonly SignJob[], err: Error): void {
  for (const job of jobs) {
    disarm(job);
    job.reject(err);
  }
}

/**
 * A worker plus its slot accounting. Holding in-flight jobs on the worker rather than in one pool-wide map
 * makes "how full is this worker" and "which jobs die with it" the same fact, so they cannot disagree.
 */
export interface PoolWorker {
  readonly worker: Worker;
  /** Echoed into the worker's own logs so concurrent workers can be told apart. */
  readonly label: string;
  readonly inFlight: Map<number, SignJob>;
}
/** What the dispatcher needs from whoever owns the workers. */
export interface DispatcherHost {
  /**
   * The dispatcher has stopped routing to this worker. The host terminates it and decides whether to replace
   * it, then calls failQueuedIfStranded() -- once that decision is made, the queue may have nowhere to go.
   * `wasRouting` is false for a worker that never joined, so the host can skip replacing one it never had.
   */
  workerLost(pw: PoolWorker, cause: Error, wasRouting: boolean): void;
  /** True while at least one worker is still starting and could yet report READY. */
  hasWorkersStarting(): boolean;
}

export class SignDispatcher {
  private readonly workers: PoolWorker[] = [];
  /** Jobs waiting for a slot. Only pump() takes from here, and only handOff() puts them on a worker. */
  private readonly queue: SignJob[] = [];
  private nextId = 1;

  constructor(private readonly host: DispatcherHost) {}

  /**
   * Whether a sign can be dispatched right now. Derived from the list rather than held as a flag: warmUp
   * runs once, so a flag would never be restored after the pool recovered from losing every worker.
   */
  public isReady(): boolean {
    return this.workers.length > 0;
  }

  /** How many workers are taking jobs, for the host's sizing decisions and log lines. */
  public workerCount(): number {
    return this.workers.length;
  }

  /** The workers currently taking jobs, so the host can terminate the threads it owns. */
  public activeWorkers(): readonly PoolWorker[] {
    return this.workers;
  }

  /** A worker reported READY. Joining the list IS becoming ready, so nothing needs a separate signal. */
  public addWorker(pw: PoolWorker): void {
    this.workers.push(pw);
    this.pump();
  }

  /**
   * Queue one sign. Rejects on a per-sign failure or an expired Midway session (as MidwayAuthRequiredError);
   * the caller turns any rejection into main-thread signing.
   */
  public submit(input: PiezoGetPiezoExecutionContextInput): Promise<string> {
    const id = this.nextId++;
    return new Promise<string>((resolve, reject) => {
      this.queue.push({ id, input, resolve, reject, attempts: 0 });
      this.pump();
    });
  }

  /**
   * Stop routing to this worker and settle the jobs it held. Returns whether it had been taking jobs.
   *
   * `culpritId` names the job whose deadline fired: it fails, and its slot-mates are re-queued within
   * MAX_JOB_ATTEMPTS. A crash names no job, so every job on that worker fails.
   */
  public removeWorker(pw: PoolWorker, cause: Error, culpritId?: number): boolean {
    const index = this.workers.indexOf(pw);
    const wasRouting = index !== -1;
    if (wasRouting) {
      this.workers.splice(index, 1);
    }
    const requeueSlotMates = culpritId !== undefined;
    for (const job of Array.from(pw.inFlight.values())) {
      disarm(job);
      pw.inFlight.delete(job.id);
      if (requeueSlotMates && job.id !== culpritId && job.attempts < MAX_JOB_ATTEMPTS) {
        this.queue.unshift(job);
      } else {
        job.reject(cause);
      }
    }
    this.pump();
    return wasRouting;
  }

  /** Match a worker's reply to one of its in-flight jobs, settle it, and refill the slot it freed. */
  public handleWorkerMessage(pw: PoolWorker, msg: WorkerToPool): void {
    if (msg.type !== 'RESULT' && msg.type !== 'ERROR') {
      return; // READY / INIT_ERROR belong to the host's spawn-scoped listener
    }
    const job = this.takeInFlight(pw, msg.id);
    if (!job) {
      return; // already timed out, or this worker was evicted
    }
    if (msg.type === 'RESULT') {
      job.resolve(msg.artifactBundle);
    } else if (msg.code === 'MIDWAY_AUTH_REQUIRED') {
      job.reject(new MidwayAuthRequiredError(msg.message));
    } else {
      job.reject(new Error(msg.message ?? msg.code));
    }
    this.pump();
  }

  /**
   * Fail the queue when nothing is ready and nothing is starting. Queued jobs carry no deadline, so this is
   * their only route out; the host must also call it whenever it declines to replace a lost worker.
   */
  public failQueuedIfStranded(): void {
    if (this.queue.length === 0 || this.isReady() || this.host.hasWorkersStarting()) {
      return;
    }
    this.failQueued(new Error('PiezoWorkerPool: no worker is left to run queued signs'));
  }

  /** Reject everything still queued. Used when the pool cannot serve, and on teardown. */
  public failQueued(err: Error): void {
    if (this.queue.length === 0) {
      return;
    }
    console.warn(
      `${PiezoDebugPrefix} PiezoWorkerPool: releasing ${this.queue.length} queued sign(s) to the ` +
        `caller — ${err.message}`,
    );
    failJobs(this.queue.splice(0, this.queue.length), err);
  }

  /** Fail everything, queued and in flight, and forget every worker. The host terminates them. */
  public shutDown(err: Error): void {
    this.failQueued(err);
    for (const pw of this.workers) {
      failJobs(Array.from(pw.inFlight.values()), err);
      pw.inFlight.clear();
    }
    this.workers.length = 0; // also un-readies the dispatcher (see isReady)
  }

  /**
   * The pool's only dispatch point. Called whenever the supply of slots or jobs changes: a new job, a
   * finished job, a worker joining, a worker being removed.
   */
  private pump(): void {
    for (;;) {
      if (this.queue.length === 0) {
        return;
      }
      const target = this.leastLoadedWorker();
      if (!target) {
        return; // every slot is full; the next completion or new worker will pump again
      }
      this.handOff(target, this.queue.shift() as SignJob);
    }
  }

  /**
   * The worker with the fewest jobs in flight, or undefined if every slot is full. Least-loaded rather than
   * first-with-a-free-slot, because pump() runs per arriving job and would otherwise fill worker 0 first.
   */
  private leastLoadedWorker(): PoolWorker | undefined {
    let best: PoolWorker | undefined;
    for (const pw of this.workers) {
      if (pw.inFlight.size >= SLOTS_PER_WORKER) {
        continue;
      }
      if (!best || pw.inFlight.size < best.inFlight.size) {
        best = pw;
      }
    }
    return best;
  }

  /** Give one job to one worker and start its deadline. The only path from queued to running. */
  private handOff(pw: PoolWorker, job: SignJob): void {
    job.attempts++;
    job.timer = setTimeout(() => {
      const cause = new Error(`Piezo sign timed out after ${SIGN_TIMEOUT_MS}ms on ${pw.label}`);
      const wasRouting = this.removeWorker(pw, cause, job.id);
      this.host.workerLost(pw, cause, wasRouting);
    }, SIGN_TIMEOUT_MS);
    pw.inFlight.set(job.id, job);
    const sign: PoolToWorker = { id: job.id, type: 'SIGN', input: job.input };
    pw.worker.postMessage(sign);
  }

  /** Detach a job from its worker and stop its deadline. Returns undefined if it already settled. */
  private takeInFlight(pw: PoolWorker, id: number): SignJob | undefined {
    const job = pw.inFlight.get(id);
    if (!job) {
      return undefined;
    }
    disarm(job);
    pw.inFlight.delete(id);
    return job;
  }
}
